
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GroupLeaderRequest } from "@/types/admin-requests";
import { logError } from "@/utils/sessionManager";

/**
 * Hook to manage admin request actions (approve, reject, delete, send invitation)
 */
export const useRequestActions = (
  setProcessingId: (id: string | null) => void,
  fetchRequests: () => Promise<void>
) => {
  const { toast } = useToast();

  const handleApprove = useCallback(async (request: GroupLeaderRequest) => {
    setProcessingId(request.id);
    try {
      // Check if domain is already whitelisted
      const domain = request.email.split('@')[1];
      const { data: existingDomain } = await supabase
        .from('whitelisted_domains')
        .select('domain')
        .eq('domain', domain)
        .single();

      // Only add domain if it's not already whitelisted
      if (!existingDomain) {
        const { error: whitelistError } = await supabase
          .from('whitelisted_domains')
          .insert({ domain });

        if (whitelistError && whitelistError.code !== '23505') { // Ignore unique violation
          throw whitelistError;
        }
      }

      // Update request status
      const { error: updateError } = await supabase
        .from('group_leader_requests')
        .update({ 
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: (await supabase.auth.getSession()).data.session?.user.id
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Send invitation email using edge function, now including the requestId
      const response = await fetch(`https://xnrhzmdxvbmxzbotwenm.supabase.co/functions/v1/send-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: request.email,
          fullName: request.full_name,
          requestId: request.id
        }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        console.error('Invitation error response:', responseData);
        throw new Error(responseData.error || 'Failed to send invitation email');
      }

      toast({
        title: "Förfrågan godkänd",
        description: `${request.full_name} har godkänts som gruppledare och fått en inbjudan via e-post`,
      });

      fetchRequests();
    } catch (error: any) {
      console.error("Error approving request:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  }, [setProcessingId, fetchRequests, toast]);

  const handleReject = useCallback(async (request: GroupLeaderRequest) => {
    setProcessingId(request.id);
    try {
      const { error } = await supabase
        .from('group_leader_requests')
        .update({ status: 'rejected' })
        .eq('id', request.id);

      if (error) throw error;

      toast({
        title: "Förfrågan avslagen",
        description: `${request.full_name}s förfrågan har avslagits`,
      });

      fetchRequests();
    } catch (error: any) {
      console.error("Error rejecting request:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  }, [setProcessingId, fetchRequests, toast]);

  const handleDelete = useCallback(async (request: GroupLeaderRequest) => {
    setProcessingId(request.id);
    try {
      const { error } = await supabase
        .from('group_leader_requests')
        .delete()
        .eq('id', request.id);

      if (error) throw error;

      toast({
        title: "Gruppledare borttagen",
        description: `${request.full_name} har tagits bort från systemet`,
      });

      fetchRequests();
    } catch (error: any) {
      console.error("Error deleting request:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  }, [setProcessingId, fetchRequests, toast]);

  const handleSendInvitation = useCallback(async (request: GroupLeaderRequest) => {
    console.log('Sending invitation for request:', request);
    setProcessingId(request.id);
    try {
      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      console.log('Using session token for request:', session.access_token.slice(0, 10) + '...');

      // Send invitation email using edge function
      const response = await fetch(`https://xnrhzmdxvbmxzbotwenm.supabase.co/functions/v1/send-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: request.email,
          fullName: request.full_name,
          requestId: request.id
        }),
      });

      const responseData = await response.json();
      console.log('Invitation response:', responseData);
      
      if (!response.ok) {
        console.error('Invitation error response:', responseData);
        throw new Error(responseData.error || 'Failed to send invitation email');
      }

      toast({
        title: "Inbjudan skickad",
        description: `${request.full_name} har fått en inbjudan via e-post`,
      });

      fetchRequests();
    } catch (error: any) {
      logError(error, "sendInvitation");
      console.error("Error sending invitation:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  }, [setProcessingId, fetchRequests, toast]);

  const handleResetPassword = useCallback(async (request: GroupLeaderRequest) => {
    setProcessingId(request.id);
    try {
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: request.email },
      });

      if (error) throw error;

      toast({
        title: "Återställningslänk skickad",
        description: `${request.full_name} har fått ett mail för att återställa lösenordet`,
      });
    } catch (error: any) {
      logError(error, "resetPassword");
      console.error("Error sending password reset:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  }, [setProcessingId, toast]);

  return {
    handleApprove,
    handleReject,
    handleDelete,
    handleSendInvitation,
    handleResetPassword,
  };
};
